import { FC, useEffect, useState } from "react";
import { ApiResponse, Page, User } from "../types";
import api from "../services/api";
import {Client, useData} from "../context/DataContext";
import InviteUserModal from "../components/InvitedUserModal";

const ConfirmationModal: FC<{
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmText?: string;
    confirmColor?: string;
}> = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Conferma", confirmColor = "bg-red-600 hover:bg-red-500" }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex justify-center items-center p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-md shadow-lg">
                <div className="p-6">
                    <h3 className="text-lg font-bold text-white">{title}</h3>
                    <p className="text-slate-400 mt-2">{message}</p>
                </div>
                <div className="p-4 bg-slate-900/50 flex justify-end space-x-4">
                    <button onClick={onClose} className="px-4 py-2 rounded-lg text-white font-semibold bg-slate-700 hover:bg-slate-600">Annulla</button>
                    <button onClick={onConfirm} className={`px-4 py-2 rounded-lg text-white font-semibold ${confirmColor}`}>{confirmText}</button>
                </div>
            </div>
        </div>
    );
};

const UsersPage: FC = () => {
    const [usersPage, setUsersPage] = useState<Page<User> | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [modalState, setModalState] = useState<{
        isOpen: boolean;
        action: (() => void) | null;
        title: string;
        message: string;
        confirmText?: string;
        confirmColor?: string;
    }>({ isOpen: false, action: null, title: '', message: '' });

    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const { clients } = useData();

    const fetchUsers = async (currentPage = 0) => {
        setIsLoading(true);
        try {
            const response = await api.get<ApiResponse<Page<User>>>(`/api/users/confirmed`, {
                params: { page: currentPage, size: 10, approve: -1, clientId: -1 }
            });
            setUsersPage(response.data.data);
        } catch (error) {
            console.error("Errore nel caricamento degli utenti:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers(page);
    }, [page]);

    const handleApprove = async (userId: number, shouldApprove: boolean) => {
        closeModal();
        try {
            await api.get(`/api/users/approve/${userId}/${shouldApprove ? 1 : 0}`);
            fetchUsers(page);
        } catch (error) {
            console.error("Errore durante l'operazione di approvazione/rifiuto:", error);
        }
    };

    const handleDelete = async (userId: number) => {
        closeModal();
        try {
            await api.delete(`/api/users/delete/${userId}`);
            if (usersPage && usersPage.content.length === 1 && page > 0) {
                setPage(page - 1);
            } else {
                fetchUsers(page);
            }
        } catch (error) {
            console.error("Errore durante l'eliminazione:", error);
        }
    };

    const openModal = (user: User, actionType: 'approve' | 'disapprove' | 'delete') => {
        let config = { isOpen: true, action: () => {}, title: '', message: '', confirmText: '', confirmColor: '' };
        switch (actionType) {
            case 'approve':
                config = { ...config, action: () => handleApprove(user.id, true), title: 'Approva Utente', message: `Sei sicuro di voler approvare l'utente ${user.name} ${user.surname}?`, confirmText: 'Approva', confirmColor: 'bg-green-600 hover:bg-green-500' };
                break;
            case 'disapprove':
                config = { ...config, action: () => handleApprove(user.id, false), title: 'Rifiuta Utente', message: `Sei sicuro di voler rifiutare e disattivare l'utente ${user.name} ${user.surname}?`, confirmText: 'Rifiuta', confirmColor: 'bg-amber-600 hover:bg-amber-500' };
                break;
            case 'delete':
                config = { ...config, action: () => handleDelete(user.id), title: 'Elimina Utente', message: `Sei sicuro di voler eliminare definitivamente l'utente ${user.name} ${user.surname}? L'azione è irreversibile.`, confirmText: 'Elimina', confirmColor: 'bg-red-600 hover:bg-red-500' };
                break;
        }
        setModalState(config);
    };

    const closeModal = () => setModalState({ isOpen: false, action: null, title: '', message: '' });

    return (
        <div>
            <ConfirmationModal
                {...modalState}
                onClose={closeModal}
                onConfirm={() => modalState.action && modalState.action()}
            />
            <InviteUserModal
                isOpen={isInviteModalOpen}
                onClose={() => setIsInviteModalOpen(false)}
                clients={clients}
            />

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div><h1 className="text-3xl font-bold text-white mb-2">Gestione Utenti</h1><p className="text-slate-400">Approva, modifica o elimina gli utenti registrati.</p></div>
                <button onClick={() => setIsInviteModalOpen(true)} className="px-5 py-2.5 rounded-lg text-white font-semibold bg-sky-600 hover:bg-sky-500 transition-colors flex items-center space-x-2 self-start md:self-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
                    <span>Invita Utente</span>
                </button>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="text-xs text-slate-400 uppercase bg-slate-800">
                        <tr>
                            <th className="p-4">Nome</th>
                            <th className="p-4 hidden sm:table-cell">Email</th>
                            <th className="p-4 hidden sm:table-cell">Cliente</th>
                            <th className="p-4 hidden md:table-cell">Ruolo</th>
                            <th className="p-4">Stato</th>
                            <th className="p-4 text-right">Azioni</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700">
                        {isLoading ? (
                            <tr><td colSpan={5} className="text-center p-8 text-slate-400">Caricamento...</td></tr>
                        ) : !usersPage || usersPage.content.length === 0 ? (
                            <tr><td colSpan={5} className="text-center p-8 text-slate-400">Nessun utente trovato.</td></tr>
                        ) : (
                            usersPage.content.map(user => (
                                <tr key={user.id} className="hover:bg-slate-800 transition-colors">
                                    <td className="p-4 text-white font-medium">{user.name} {user.surname}</td>
                                    <td className="p-4 text-slate-300 hidden sm:table-cell">{user.email}</td>
                                    <td className="p-4 text-slate-300 hidden sm:table-cell">{(!user.client || user.client === "") ? "N/A" : user.client}</td>
                                    <td className="p-4 text-slate-300 hidden md:table-cell">{user.role}</td>
                                    <td className="p-4">
                                        {user.approved
                                            ? <span
                                                className="px-2 py-1 text-xs font-semibold text-green-200 bg-green-500/20 rounded-full">Approvato</span>
                                            : <span
                                                className="px-2 py-1 text-xs font-semibold text-amber-200 bg-amber-500/20 rounded-full">In Attesa</span>
                                        }
                                    </td>
                                    <td className="p-4 text-right space-x-2">
                                        {!user.approved && (
                                            <button onClick={() => openModal(user, 'approve')}
                                                    className="text-green-400 hover:text-green-300 text-sm font-semibold">Approva</button>
                                        )}
                                        <button onClick={() => openModal(user, 'delete')}
                                                className="text-red-400 hover:text-red-300 text-sm font-semibold">Elimina
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>
                {usersPage && usersPage.totalPages > 1 && (
                    <div className="p-4 flex justify-between items-center text-sm text-slate-400">
                        <button onClick={() => setPage(p => p - 1)} disabled={usersPage.number === 0 || isLoading} className="px-3 py-1 rounded-md bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed">Precedente</button>
                        <span>Pagina {usersPage.number + 1} di {usersPage.totalPages}</span>
                        <button onClick={() => setPage(p => p + 1)} disabled={usersPage.number >= usersPage.totalPages - 1 || isLoading} className="px-3 py-1 rounded-md bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed">Successiva</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UsersPage;
